/**
 * Public Invoice View (Feb 2026) — vista pública SOLO LECTURA.
 * Se monta desde App.js cuando la URL trae `?i=<share_token>`.
 * Mismo patrón seguro que PublicEstimateView (token secreto, sin sesión).
 * Consume GET /api/public/invoices/{token} (payload ya sanitizado).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { PublicGroupedItemsBody } from "@/components/PublicGroupedItems";
import { PartnerPointBlock } from "@/components/PartnerPointBlock";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export default function PublicInvoiceView({ token }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [square, setSquare] = useState(null); // {url, amount} | null

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/invoices/${encodeURIComponent(token)}`
        );
        setDoc(data);
        // Sprint Square — enlace de pago solo si hay balance pendiente.
        if (!data.cancelled && Number(data.balance_due || 0) > 0) {
          try {
            const res = await axios.post(
              `${API}/public/invoices/${encodeURIComponent(token)}/square-link`
            );
            setSquare(res.data);
          } catch {
            setSquare(null); // no configurado / sin balance → sección oculta
          }
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg,#f5f5f0)]">
        <div className="font-mono text-sm text-neutral-500 animate-pulse">
          Cargando documento…
        </div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div
        data-testid="public-invoice-error"
        className="min-h-screen flex items-center justify-center bg-[var(--bg,#f5f5f0)] p-6"
      >
        <div className="bg-white border-2 border-black p-8 max-w-md text-center">
          <div className="text-3xl mb-3">🔒</div>
          <h1 className="font-bold text-lg">Documento no disponible</h1>
          <p className="text-sm text-neutral-600 mt-2">
            Este enlace no es válido o el documento ya no está disponible.
            Contactá a quien te lo compartió para recibir un enlace nuevo.
          </p>
        </div>
      </div>
    );
  }

  if (doc.cancelled) {
    return (
      <div
        data-testid="public-invoice-cancelled"
        className="min-h-screen flex items-center justify-center bg-[var(--bg,#f5f5f0)] p-6"
      >
        <div className="bg-white border-2 border-black p-8 max-w-md text-center">
          <div className="text-3xl mb-3">⚠️</div>
          <h1 className="font-bold text-lg">Factura cancelada / Invoice cancelled</h1>
          <p className="text-sm text-neutral-600 mt-2">
            La factura {doc.number || ""} fue cancelada y ya no está disponible.
            Si crees que es un error, contacta a Rawen&apos;s Printing &amp; Designs.
          </p>
        </div>
      </div>
    );
  }

  const c = doc.company_info || {};
  const cust = doc.customer || {};
  const showDiscount = Number(doc.discount_amount || 0) > 0;
  const showCredit = Number(doc.credit_notes_total || 0) > 0;
  const payments = doc.payments || [];

  return (
    <div
      data-testid="public-invoice-view"
      className="min-h-screen bg-[var(--bg,#f5f5f0)] py-8 px-4"
    >
      <div className="max-w-3xl mx-auto bg-white border-2 border-black">
        {/* Header empresa */}
        <div className="px-6 sm:px-8 py-6 border-b-2 border-black flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {c.logo_url && (
              <img
                src={c.logo_url}
                alt={c.name || "Logo"}
                className="w-16 h-16 object-contain"
              />
            )}
            <div>
              <div data-testid="public-inv-company" className="font-black text-xl tracking-tight">
                {c.name || ""}
              </div>
              <div className="text-xs text-neutral-600">
                {[c.office_name, c.office_address].filter(Boolean).join(" · ")}
              </div>
              <div className="text-xs text-neutral-600">
                {[c.office_phone, c.office_email, c.website]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase">
              Factura
            </div>
            <div data-testid="public-inv-number" className="font-black text-2xl tracking-tight">
              {doc.number}
            </div>
            <div data-testid="public-inv-date" className="text-xs text-neutral-600 mt-1">
              {fmtDate(doc.created_at)}
            </div>
          </div>
        </div>

        {/* Cliente */}
        <div className="px-6 sm:px-8 py-4 border-b border-neutral-200">
          <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mb-1">
            Facturado a
          </div>
          <div data-testid="public-inv-customer" className="font-bold">
            {cust.company || ""}
          </div>
          {cust.contact_person && (
            <div className="text-sm text-neutral-600">{cust.contact_person}</div>
          )}
          {doc.partner_point && (
            <div className="mt-3">
              <PartnerPointBlock pp={doc.partner_point} compact />
            </div>
          )}
          {doc.pickup && (
            <div data-testid="public-pickup-block" className="mt-4 border-2 border-black bg-amber-50 p-4">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600">
                Recogida en Partner Point / Pickup
              </div>
              {doc.pickup.status === "delivered" ? (
                <div data-testid="public-pickup-delivered" className="mt-1 font-bold text-emerald-700">
                  Pedido entregado / Order delivered
                  {doc.pickup.delivered_at ? ` · ${String(doc.pickup.delivered_at).slice(0, 10)}` : ""}
                </div>
              ) : (
                <>
                  <div className="text-sm mt-1">
                    Presenta este PIN al recoger tu pedido / Show this PIN at pickup:
                  </div>
                  <div data-testid="public-pickup-pin" className="font-mono text-3xl font-black tracking-[0.4em] mt-1">
                    {doc.pickup.pin}
                  </div>
                  <div className="text-xs text-neutral-600 mt-1">
                    {doc.pickup.status === "received"
                      ? "Tu pedido ya está en el establecimiento / Ready for pickup"
                      : "Tu pedido va en camino al establecimiento / On its way"}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Items */}
        <div className="px-6 sm:px-8 py-4">
          <table data-testid="public-inv-items" className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-black text-left">
                <th className="py-2 pr-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500">
                  Descripción
                </th>
                <th className="py-2 px-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500 text-right">
                  Cant.
                </th>
                <th className="py-2 px-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500 text-right">
                  Precio
                </th>
                <th className="py-2 pl-2 font-mono text-[10px] tracking-[0.15em] uppercase text-neutral-500 text-right">
                  Total
                </th>
              </tr>
            </thead>
            <PublicGroupedItemsBody items={doc.items} testidPrefix="public-inv" />
          </table>

          {/* Totales */}
          <div className="flex justify-end mt-4">
            <div className="w-full sm:w-80 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600">Subtotal</span>
                <span data-testid="public-inv-subtotal" className="font-mono">
                  {money(doc.subtotal)}
                </span>
              </div>
              {showDiscount && (
                <div className="flex justify-between">
                  <span className="text-neutral-600">Descuento</span>
                  <span data-testid="public-inv-discount" className="font-mono">
                    −{money(doc.discount_amount)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-neutral-600">
                  Impuesto{doc.tax_exempt ? " (exento)" : doc.sales_tax_rate ? ` (${+(doc.sales_tax_rate * 100).toFixed(2)}%)` : ""}
                </span>
                <span data-testid="public-inv-tax" className="font-mono">
                  {money(doc.sales_tax_amount)}
                </span>
              </div>
              <div className="flex justify-between border-t-2 border-black pt-2 mt-2">
                <span className="font-black text-base">TOTAL</span>
                <span data-testid="public-inv-total" className="font-black font-mono text-base">
                  {money(doc.total_amount)}
                </span>
              </div>
              <div className="flex justify-between pt-1">
                <span className="text-neutral-600">Pagos recibidos</span>
                <span data-testid="public-inv-payments-total" className="font-mono">
                  −{money(doc.payments_total)}
                </span>
              </div>
              {showCredit && (
                <div className="flex justify-between">
                  <span className="text-neutral-600">Crédito aplicado</span>
                  <span data-testid="public-inv-credit" className="font-mono">
                    −{money(doc.credit_notes_total)}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t-2 border-black pt-2 mt-2">
                <span className="font-black text-base">BALANCE PENDIENTE</span>
                <span data-testid="public-inv-balance" className="font-black font-mono text-base">
                  {money(doc.balance_due)}
                </span>
              </div>
            </div>
          </div>

          {/* Pagos recibidos (detalle) */}
          {payments.length > 0 && (
            <div className="mt-6">
              <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mb-2">
                Pagos recibidos
              </div>
              <table data-testid="public-inv-payments" className="w-full text-sm">
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={i} className="border-b border-neutral-200">
                      <td className="py-1.5 pr-2 font-mono text-xs">{p.date}</td>
                      <td className="py-1.5 px-2 text-neutral-600">{p.method}</td>
                      <td className="py-1.5 pl-2 text-right font-mono">
                        {money(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sprint Square — pago en línea con QR (solo con balance pendiente) */}
        {square && Number(doc.balance_due || 0) > 0 && (
          <div
            data-testid="public-inv-square"
            className="px-6 sm:px-8 py-5 border-t border-neutral-200 text-center"
          >
            <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mb-2">
              Paga en línea / Pay online
            </div>
            <img
              data-testid="public-inv-square-qr"
              src={`${API}/public/invoices/${encodeURIComponent(token)}/square-qr.png`}
              alt="QR de pago Square"
              className="mx-auto w-40 h-40 border border-neutral-300"
            />
            <p className="text-xs text-neutral-600 mt-2">
              Escanea el código con tu teléfono o presiona el botón para pagar el
              balance de {money(doc.balance_due)} con Square.
            </p>
            <a
              data-testid="public-inv-square-btn"
              href={square.url}
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-3 px-6 py-3 bg-black text-white text-sm font-bold hover:bg-neutral-800"
            >
              Pagar ahora / Pay now
            </a>
          </div>
        )}

        {/* Notas para el cliente */}
        {doc.customer_notes && (
          <div className="px-6 sm:px-8 py-4 border-t border-neutral-200">
            <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mb-1">
              Notas
            </div>
            <p data-testid="public-inv-notes" className="text-sm whitespace-pre-wrap">
              {doc.customer_notes}
            </p>
          </div>
        )}

        <div className="px-6 sm:px-8 py-4 border-t-2 border-black text-center">
          <p className="text-xs text-neutral-500">
            Documento generado por {c.name || "GP"} · Solo lectura
          </p>
        </div>
      </div>
    </div>
  );
}
